import { useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { SignInButton } from '@clerk/clerk-react';
import { useClerkSession } from '@/hooks/useClerkSession';
import toast from 'react-hot-toast';

import { useLeagueSettings } from '@/context/LeagueSettingsContext';
import { normalizeText, teamAliases } from '@/utils/teams';
import { RosterNotes } from '@/components/RosterNotes';
import { useMultiRoster } from '@/context/MultiRosterContext';
import { RosterSelector } from '@/components/RosterSelector';
import { MyRosterHeader } from '@/components/MyRosterHeader';
import { PlayerSearch, type PlayerSearchRef } from '@/components/PlayerSearch';
import { PositionGroup } from '@/components/PositionGroup';
import { SleeperImport } from '@/components/SleeperImport';
import type { Player } from '@/types/player';
import type { LeagueSettings as TradeLeagueSettings } from '@/types/trade';
import { positionSlotCount, ROSTER_POSITIONS } from '@/utils/rosterDisplay';
import { PageShell } from '@/components/ui/PageShell';
import { PageLoader } from '@/components/LoadingSpinner';
import { Surface } from '@/components/ui/Surface';

export const MyRosterContent = () => {
  const { isLoaded, signedIn } = useClerkSession();
  const {
    activeRoster,
    activeRosterPlayers,
    playersPool,
    addPlayerToRoster,
    removePlayerFromRoster,
    replaceRosterPlayersWithStarters,
    togglePlayerStarter,
    isPlayerInActiveRoster,
  } = useMultiRoster();
  const { leagueData, updateFromSleeper, hasImportedSettings } = useLeagueSettings();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [positionFilter, setPositionFilter] = useState<string | null>(null);
  const [showPositionSearch, setShowPositionSearch] = useState(false);
  const [mobileTab, setMobileTab] = useState<'roster' | 'notes'>('roster');
  const playerSearchRef = useRef<PlayerSearchRef>(null);

  const getRosterByPosition = useCallback(() => {
    const settings = activeRoster?.format.roster || leagueData.settings.roster;
    return ROSTER_POSITIONS.map((pos) => {
      const players = activeRosterPlayers.filter((player) => player.position === pos);
      return {
        position: pos,
        players,
        hasSlots: positionSlotCount(pos, settings) > 0,
      };
    });
  }, [activeRoster, activeRosterPlayers, leagueData.settings.roster]);

  const handleSearch = useCallback((query: string, filterPosition?: string) => {
    setSearchQuery(query);
    if (query.length < 2 && !filterPosition) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    const q = normalizeText(query);
    const results = playersPool
      .filter((player) => {
        if (isPlayerInActiveRoster(String(player.id))) return false;
        if (filterPosition && player.position !== filterPosition) return false;
        if (filterPosition && query.length < 2) return true;
        const base = normalizeText(player.name || '');
        if (base.includes(q)) return true;
        if (player.position === 'DEF') {
          const aliases = teamAliases(player.team);
          if (aliases.some((alias) => alias && (alias.includes(q) || q.includes(alias)))) return true;
        }
        return false;
      })
      .slice(0, 15);
    setSearchResults(results);
    setIsSearching(false);
  }, [playersPool, isPlayerInActiveRoster]);

  const handlePositionSearch = useCallback((position: string) => {
    setPositionFilter(position);
    setShowPositionSearch(true);
    setSearchQuery('');
    handleSearch('', position);
    playerSearchRef.current?.scrollToSearch();
  }, [handleSearch]);

  const clearPositionFilter = useCallback(() => {
    setPositionFilter(null);
    setShowPositionSearch(false);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  const handleAddPlayer = useCallback(async (player: Player) => {
    if (!activeRoster) {
      toast.error('Create a roster first.');
      return;
    }
    await addPlayerToRoster(activeRoster.id, player);
    toast.success(`${player.name} added`);
    setSearchQuery('');
    setSearchResults([]);
    if (positionFilter === player.position) clearPositionFilter();
  }, [activeRoster, addPlayerToRoster, positionFilter, clearPositionFilter]);

  const handleSleeperImport = useCallback(async (
    players: Player[],
    leagueSettings?: TradeLeagueSettings,
    leagueName?: string,
    starterPlayerIds?: string[]
  ) => {
    if (players.length === 0 || !activeRoster) {
      toast.error(activeRoster ? 'No players came back from that league.' : 'Create a roster first.');
      return;
    }
    await replaceRosterPlayersWithStarters(activeRoster.id, players, starterPlayerIds);
    if (leagueSettings && leagueName) updateFromSleeper(leagueSettings, leagueName);
    toast.success(`Imported ${players.length} players`);
  }, [activeRoster, replaceRosterPlayersWithStarters, updateFromSleeper]);

  if (!isLoaded) {
    return <PageLoader pageName="My Roster" loadingText="Loading..." />;
  }

  if (!signedIn) {
    return (
      <PageShell>
        <Helmet>
          <title>My Roster | Fantasy Fiesta</title>
        </Helmet>
        <div className="mx-auto max-w-lg py-8 text-center">
          <h1 className="font-display text-3xl font-semibold text-white sm:text-5xl">My Roster</h1>
          <p className="mt-4 text-base text-zinc-400 sm:text-lg">
            Sign in to keep a team and get weekly notes that match the players you actually own.
          </p>
          <SignInButton mode="modal">
            <button
              type="button"
              className="mt-8 w-full rounded-full bg-fiesta-yellow px-8 py-3.5 text-base font-semibold text-ink sm:w-auto"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </PageShell>
    );
  }

  const scoring = leagueData.settings.scoring.PPR
    ? (leagueData.settings.scoring.pointsPerReception === 0.5 ? 'Half PPR' : 'PPR')
    : 'Standard';

  const notesCard = activeRosterPlayers.length > 0 ? (
    <RosterNotes roster={activeRosterPlayers} scoring={scoring} />
  ) : null;

  const rosterBody = !activeRoster ? (
    <Surface className="p-6 text-center sm:p-8">
      <h2 className="font-display text-xl font-semibold text-white">Create a roster</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
        Name a team above, then import from Sleeper or add players by search.
      </p>
    </Surface>
  ) : activeRosterPlayers.length === 0 ? (
    <Surface className="p-6 text-center sm:p-8">
      <h2 className="font-display text-xl font-semibold text-white">This roster is empty</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-zinc-400">
        Use Import from Sleeper or Other imports above, or search a name.
      </p>
    </Surface>
  ) : (
    <Surface className="border-white/15 bg-black/45 px-3 py-3 shadow-xl shadow-black/20 backdrop-blur-xl sm:px-4 sm:py-4">
      {getRosterByPosition()
        .filter(({ hasSlots }) => hasSlots)
        .map(({ position, players }) => (
          <PositionGroup
            key={position}
            position={position}
            players={players}
            onAddPlayer={handlePositionSearch}
            onRemovePlayer={(playerId) => activeRoster && removePlayerFromRoster(activeRoster.id, playerId)}
            onToggleStarter={(playerId, isStarter) => (
              activeRoster && togglePlayerStarter(activeRoster.id, playerId, isStarter)
            )}
            starterPlayerIds={activeRoster?.starters || []}
          />
        ))}
    </Surface>
  );

  return (
    <PageShell className="roster-page">
      <Helmet>
        <title>My Roster | Fantasy Fiesta</title>
        <meta name="description" content="Your fantasy roster with weekly notes from the live web." />
      </Helmet>

      <MyRosterHeader
        hasImportedSettings={hasImportedSettings}
        leagueName={leagueData.leagueName}
      />

      <div className="mx-auto max-w-6xl space-y-4">

        {activeRosterPlayers.length > 0 ? (
          <div className="grid grid-cols-2 gap-1 rounded-full border border-white/20 bg-black/40 p-0.5 backdrop-blur-xl lg:hidden">
            {(['roster', 'notes'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setMobileTab(tab)}
                className={`rounded-full py-1.5 text-sm ${
                  mobileTab === tab ? 'bg-white/15 text-white shadow-sm' : 'text-zinc-300'
                }`}
              >
                {tab === 'roster' ? 'Lineup' : 'Notes'}
              </button>
            ))}
          </div>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(260px,0.8fr)] lg:items-start">
          <div className={activeRosterPlayers.length > 0 && mobileTab !== 'roster' ? 'hidden lg:block' : 'space-y-3'}>
            <RosterSelector
              extraActions={(
                <>
                  {activeRoster ? (
                    <SleeperImport
                      onImportSuccess={handleSleeperImport}
                      availablePlayers={playersPool}
                    />
                  ) : null}
                  <Link
                    to="/settings#other-imports"
                    className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10"
                  >
                    Other imports
                  </Link>
                  <Link
                    to="/settings#league-rules"
                    className="inline-flex min-h-10 items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-white transition-colors hover:border-white/30 hover:bg-white/10"
                  >
                    League settings
                  </Link>
                </>
              )}
            />

            <PlayerSearch
              ref={playerSearchRef}
              searchQuery={searchQuery}
              searchResults={searchResults}
              isSearching={isSearching}
              positionFilter={positionFilter}
              showPositionSearch={showPositionSearch}
              onSearchChange={handleSearch}
              onAddPlayer={handleAddPlayer}
              onClearPositionFilter={clearPositionFilter}
            />

            {rosterBody}
          </div>

          <div className={activeRosterPlayers.length > 0 && mobileTab !== 'notes' ? 'hidden lg:block lg:sticky lg:top-24' : 'lg:sticky lg:top-24'}>
            {notesCard}
          </div>
        </div>
      </div>
    </PageShell>
  );
};
